import LayoutContainer from 'components/Common/LayoutContainer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import useCarHireList from 'hooks/CarHire/useCarHireList'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { sortCarHireOffers } from '../carHireUtils'
import CarHireOfferTileCondensed from '../CarHireOfferTile/CarHireOfferTileCondensed'
import Group from 'components/utils/Group'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { rem } from 'polished'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import TextButton from 'LuxKit/Button/TextButton'
import qs from 'qs'
import { pushWithRegion } from 'actions/NavigationActions'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { take } from 'lib/array/arrayUtils'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import styled from 'styled-components'
import moment from 'moment'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { dateDifference } from 'lib/datetime/dateUtils'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { generateCarHireCheckoutItem } from '../CarHireCheckoutHelper'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { HotelOrderData } from 'hooks/CarHire/useOrderCarHire'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import useCarHireOfferListFilters from 'hooks/CarHire/useCarHireOfferListFilters'

const expired = 0

const UpcomingTripSection = styled(Pane)`
  padding: ${rem(40)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(64)} 0;
  }
`

const LoadingWrapper = styled(Group)`
  height: ${rem(240)};
`

const DesktopLocationLabel = styled.span`
  margin-inline-start: 0.25em;
`

interface Props {
  orderView: HotelOrderData
}

function CarHirePageCarousels(props: Props) {
  const { orderView } = props
  const intl = useIntl()

  const checkIn = moment(orderView.dates.checkIn).format(DMY_CASUAL_SHORT_FORMAT)
  const checkOut = moment(orderView.dates.checkOut).format(DMY_CASUAL_SHORT_FORMAT)
  const daysToGo = dateDifference(new Date(checkIn), new Date()).days
  const pickUpName = orderView.place?.canonicalName

  const [defaultAgeCategory, defaultAge] = useAppSelector(getDefaultUserDriverCategory)
  const dispatch = useAppDispatch()

  const filters = useCarHireOfferListFilters({
    pickUpLocationId: orderView.place?.id,
    dropOffLocationId: orderView.place?.id,
    pickUpDate: checkIn || undefined,
    dropOffDate: checkOut || undefined,
    pickUpTime: '10:00',
    dropOffTime: '10:00',
    driversAgeCategory: defaultAgeCategory,
    isPickUpAtAirport: false,
    pickUpSearchType: 'geo',
    dropOffSearchType: 'geo',
    driversAge: defaultAge,
  })

  const carHireList = useCarHireList(filters, expired)
  const offers = carHireList.vehicles
  const sortedOffers = useMemo(() => take(sortCarHireOffers(offers, 'price.asc'), 6), [offers])

  const onSearch = () => {
    const query = qs.stringify({
      pickUpName,
      dropOffName: pickUpName,
      pickUpId: filters?.pickUpLocationId,
      dropOffId: filters?.dropOffLocationId,
      pickUpDate: filters?.pickUpDate,
      pickUpTime: '10:00',
      dropOffDate: filters?.dropOffDate,
      dropOffTime: '10:00',
      ageCategory: filters?.driversAgeCategory,
      age: filters?.driversAge,
      isPickUpAtAirport: false,
      pickUpSearchType: 'geo',
      dropOffSearchType: 'geo',
    })

    dispatch(pushWithRegion('/search/car-hire', query))
  }

  const onGoToCheckout = useCallback((offer: App.CarHireOffer) => {
    if (filters) {
      const [pickUpDate, pickUpTime] = moment.utc(carHireList.pickUp.date).format('YYYY-MM-DD|HH:mm').split('|')
      const [dropOffDate, dropOffTime] = moment.utc(carHireList.dropOff.date).format('YYYY-MM-DD|HH:mm').split('|')

      const item = generateCarHireCheckoutItem(offer, {
        dropOffDate,
        dropOffTime,
        dropOffLocation: pickUpName,
        dropOffLocationId: filters.pickUpLocationId!,
        pickUpLocation: pickUpName,
        pickUpLocationId: filters.pickUpLocationId!,
        pickUpDate,
        pickUpTime,
        ageCategory: filters.driversAgeCategory!,
        age: filters.driversAge!,
        isPickUpAtAirport: !!filters.isPickUpAtAirport!,
        resultsTimestamp: carHireList.timestamp || 0,
      })

      dispatch(initialiseCheckout([item]))
      Analytics.trackEvent(addToCart())
      dispatch(navigateToCheckout())
    }
  }, [dispatch, filters, pickUpName, carHireList])

  const viewAllLabel = intl.formatMessage({
    id: 'caGGSCEdfG',
    defaultMessage: 'View All',
    description: 'Button text to view all car hire offers for an upcoming trip',
  })

  const daysToGoLabel = intl.formatMessage(
    {
      id: 'U2eZ+mTKOF',
      defaultMessage: '{daysToGo, plural, one {# Day} other {# Days}} to go',
      description: 'Countdown label showing days until car hire trip in the car hire section',
    },
    { daysToGo },
  )

  const dateRangeLabel = intl.formatMessage(
    {
      id: 'GMr//5XVYU',
      defaultMessage: '{checkIn} - {checkOut}',
      description: 'Date range for car hire trip pick-up and drop-off in the car hire section',
    },
    { checkIn, checkOut },
  )

  const dateRangeWithDurationLabel = sortedOffers.length > 0 ? intl.formatMessage(
    {
      id: 'nGiIxLzxnO',
      defaultMessage: '{checkIn} - {checkOut} ({duration, plural, one {# Day} other {# Days}})',
      description: 'Date range with rental duration for car hire trip in the car hire section',
    },
    { checkIn, checkOut, duration: sortedOffers[0].duration },
  ) : ''

  const desktopPickUpDropOffLabel = pickUpName ? intl.formatMessage(
    {
      id: 'VxplWpXd2s',
      defaultMessage: '· Pick up & Drop-off at {pickUpName}',
      description: 'Pick up and drop-off location with dot separator on desktop',
    },
    { pickUpName },
  ) : ''

  const mobilePickUpDropOffLabel = pickUpName ? intl.formatMessage(
    {
      id: '+9wge4HB9h',
      defaultMessage: 'Pick up & Drop-off at {pickUpName}',
      description: 'Pick up and drop-off location on mobile in the car hire section',
    },
    { pickUpName },
  ) : ''

  const searchingLabel = intl.formatMessage({
    id: 'CxNQOuPPJ/',
    defaultMessage: 'Searching for the best offers for you!',
    description: 'Loading indicator text while searching for car hire offers',
  })

  return (
    <>
      {orderView && sortedOffers.length > 0 && (
        <UpcomingTripSection background="neutral-seven">
          <LayoutContainer>
            <VerticalSpacer gap={24}>
              <Group gap={8} direction="vertical">
                <Group direction="horizontal" horizontalAlign="space-between">
                  <Heading variant="heading3">
                    {intl.formatMessage(
                      {
                        id: '1OgMrLHeJz',
                        defaultMessage: 'Hire a car while you’re in <italic>{placeName}</italic>',
                        description: 'Heading for car hire section for an upcoming trip destination',
                      },
                      {
                        placeName: orderView.place?.name,
                        italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                      },
                    )}
                  </Heading>
                  <TextButton kind="tertiary" onClick={onSearch}>
                    {viewAllLabel}
                  </TextButton>
                </Group>
                <Group direction="vertical" tabletDirection="horizontal" wrap="wrap" gap={8} fullWidth>
                  <Group direction="horizontal" tabletVerticalAlign="center" gap={8}>
                    <StructureLabel variant="dark">{daysToGoLabel}</StructureLabel>
                    <BodyTextBlock variant="medium">
                      {sortedOffers.length <= 0 && dateRangeLabel}
                      {sortedOffers.length > 0 && dateRangeWithDurationLabel}
                      <CSSBreakpoint min="desktop" as="span">
                        <DesktopLocationLabel>{desktopPickUpDropOffLabel}</DesktopLocationLabel>
                      </CSSBreakpoint>
                    </BodyTextBlock>
                  </Group>
                  {/* mobile */}
                  <CSSBreakpoint max="tablet">
                    <BodyTextBlock variant="medium">
                      {mobilePickUpDropOffLabel}
                    </BodyTextBlock>
                  </CSSBreakpoint>
                </Group>
              </Group>

              {carHireList.fetching &&
                <LoadingWrapper gap={24} direction="vertical" fullWidth horizontalAlign="center" verticalAlign="center">
                  <Heading align="center" variant="heading5">
                    {searchingLabel}
                  </Heading>
                  <LoadingAnimation />
                </LoadingWrapper>}

              {!carHireList.fetching && (
                <CardCarousel width={rem(364)} gap={16}>
                  {!carHireList.fetching && sortedOffers.map((offer) =>
                    <CarHireOfferTileCondensed
                      key={offer.id}
                      offer={offer}
                      onReserve={onGoToCheckout}
                      pickUpDate={checkIn}
                      dropOffDate={checkOut}
                      pickUpTime="10:00"
                      dropOffTime="10:00"
                    />,
                  )}
                </CardCarousel>
              )}
            </VerticalSpacer>

          </LayoutContainer>
        </UpcomingTripSection>
      )}
    </>
  )
}

export default CarHirePageCarousels
